import {
  RastreioCancerInput,
  RastreioCancerPerguntaDbEnum,
  RastreioCancerPerguntaOpcaoEnum,
} from 'graphql/types.generated'
import { formatDate } from 'util/date/formatDate'

import { AnoSelectModel } from '../citopatologico/AnoSelectField'
import { RASTREIO_CANCER_OPCAO_LABEL } from '../model-rastreioCancer'
import {
  anamneseCirurgiaTableOptions,
  MamografiaPerguntaField,
  RequisicaoExameMamografiaFormValues,
} from './model-mamografia'

const cirurgiaAnoFields = anamneseCirurgiaTableOptions.flatMap(({ valueDireita, valueEsquerda }) => [
  valueDireita,
  valueEsquerda,
])

const MAMOGRAFIA_PERGUNTAS_DATA = new Set<RastreioCancerPerguntaDbEnum>([MamografiaPerguntaField.dataSolicitacao])

const MAMOGRAFIA_PERGUNTAS_TEXTO = new Set<RastreioCancerPerguntaDbEnum>([
  MamografiaPerguntaField.anoUltimaMamografia,
  MamografiaPerguntaField.anoRadioterapiaMamaDireita,
  MamografiaPerguntaField.anoRadioterapiaMamaEsquerda,
  MamografiaPerguntaField.nomeResponsavel,
  MamografiaPerguntaField.cns,
  MamografiaPerguntaField.numeroConselho,
  MamografiaPerguntaField.siglaNomeConselho,
  ...cirurgiaAnoFields,
])

const isEmpty = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

const extractDescricao = (value: unknown): string =>
  typeof value === 'object' ? (value as AnoSelectModel).nome : String(value)

export const convertMamografiaFormToRastreioCancer = (
  values: RequisicaoExameMamografiaFormValues
): RastreioCancerInput[] => {
  const { openedAccordions, ...perguntaValues } = values
  return Object.entries(perguntaValues)
    .filter(([, value]) => !isEmpty(value))
    .map(
      ([perguntaKey, value]): RastreioCancerInput => {
        const pergunta = perguntaKey as RastreioCancerPerguntaDbEnum

        if (MAMOGRAFIA_PERGUNTAS_DATA.has(pergunta)) {
          return { pergunta, data: (value as unknown) as LocalDate }
        }

        if (MAMOGRAFIA_PERGUNTAS_TEXTO.has(pergunta)) {
          return { pergunta, descricao: extractDescricao(value) }
        }

        if (pergunta === MamografiaPerguntaField.fezCirurgiaMama) {
          return {
            pergunta,
            opcoes: [value ? RastreioCancerPerguntaOpcaoEnum.SIM : RastreioCancerPerguntaOpcaoEnum.NAO],
          }
        }

        return {
          pergunta,
          opcoes: Array.isArray(value)
            ? (value as RastreioCancerPerguntaOpcaoEnum[])
            : [value as RastreioCancerPerguntaOpcaoEnum],
        }
      }
    )
}

export function convertRequisicaoExameMamografiaFormToMap(
  values: RequisicaoExameMamografiaFormValues
): Map<RastreioCancerPerguntaDbEnum, string> {
  const { openedAccordions, ...perguntaValues } = values

  return new Map(
    Object.entries(perguntaValues)
      .filter(([, value]) => !isEmpty(value))
      .map(([perguntaKey, value]): [RastreioCancerPerguntaDbEnum, string] => {
        const pergunta = perguntaKey as RastreioCancerPerguntaDbEnum

        if (MAMOGRAFIA_PERGUNTAS_DATA.has(pergunta)) {
          return [pergunta, formatDate((value as unknown) as LocalDate)]
        }

        if (MAMOGRAFIA_PERGUNTAS_TEXTO.has(pergunta)) {
          return [pergunta, extractDescricao(value)]
        }

        if (pergunta === MamografiaPerguntaField.fezCirurgiaMama) {
          return [pergunta, value ? 'Sim' : 'Não']
        }

        if (Array.isArray(value)) {
          return [
            pergunta,
            (value as RastreioCancerPerguntaOpcaoEnum[]).map((opcao) => RASTREIO_CANCER_OPCAO_LABEL[opcao]).join(', '),
          ]
        }

        return [pergunta, RASTREIO_CANCER_OPCAO_LABEL[value as RastreioCancerPerguntaOpcaoEnum]]
      })
  )
}
